import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  buildPlaceholderRow,
  buildReceiveRows,
  useLocationShipments,
  useShipmentBalances,
  type ReceiveChildrenById,
  type ReceiveTableRow
} from '@/entities/receive';

interface UseReceiveLazyRowsArgs {
  /** Level-0 rows from `GET /locations` — the only data present before any expand. */
  locations: ReceiveTableRow[];
  projectId?: number;
  siteId?: number;
  groupByDate: boolean;
  /**
   * Serialized filter state. Children are cached per row id for the lifetime of a
   * filter set; when it changes the cache is dropped so stale branches cannot
   * survive under a refreshed location list.
   */
  filterKey: string;
  /**
   * Bumped by the page when a write consumed data the loaded branches describe
   * (a confirmed receipt). Tag invalidation alone only refreshes the level-0
   * location counts — this drops the cached children so they are re-fetched.
   */
  dataVersion?: number;
}

/**
 * Owns expand state and the lazy fetching behind it: nothing below the location
 * level is requested until its row is opened.
 *
 * - expand a location → one location's shipments (in grouped mode that single
 *   response fills BOTH the date rows and their consignments)
 * - expand a consignment → that shipment's balance lines
 *
 * The requests themselves live in `entities/receive` (`useLocationShipments` /
 * `useShipmentBalances`), which own unwrapping, error handling and normalizing;
 * this hook only decides *when* to ask and assembles the rows.
 *
 * Results are kept in `childrenById`, so collapsing and re-expanding a row costs
 * no request.
 */
export const useReceiveLazyRows = ({
  locations,
  projectId,
  siteId,
  groupByDate,
  filterKey,
  dataVersion = 0
}: UseReceiveLazyRowsArgs) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [childrenById, setChildrenById] = useState<ReceiveChildrenById>({});
  // Rows whose fetch already ran (resolved or failed) — guards against refiring
  // on every re-expand while `childrenById` holds a placeholder.
  const requestedRef = useRef<Set<string>>(new Set());

  const { fetchLocationShipments } = useLocationShipments({ projectId, siteId });
  const { fetchShipmentBalances } = useShipmentBalances({ projectId, siteId });

  // A new filter set means a new location list; previously loaded branches belong
  // to the old one and must not leak into it. A bumped `dataVersion` means the
  // branches themselves are stale — same remedy, so the same effect handles both.
  useEffect(() => {
    requestedRef.current = new Set();
    setChildrenById({});
    setExpandedRows(new Set());
  }, [filterKey, dataVersion]);

  const setChildren = useCallback((rowId: string, rows: ReceiveTableRow[] | ReceiveChildrenById) => {
    setChildrenById((prev) => ({
      ...prev,
      ...(Array.isArray(rows) ? { [rowId]: rows } : rows)
    }));
  }, []);

  const loadLocationChildren = useCallback(
    async (row: ReceiveTableRow) => {
      if (!row.locationId) return;
      setChildren(row.id, [buildPlaceholderRow(row.id, row.level, 'loading')]);

      const children = await fetchLocationShipments({
        locationId: row.locationId,
        rowId: row.id,
        level: row.level,
        groupByDate
      });

      if (!children) {
        setChildren(row.id, [buildPlaceholderRow(row.id, row.level, 'error')]);
        return;
      }

      const direct = children[row.id] ?? [];
      setChildren(row.id, direct.length ? children : [buildPlaceholderRow(row.id, row.level, 'empty')]);
    },
    [fetchLocationShipments, groupByDate, setChildren]
  );

  const loadBalanceChildren = useCallback(
    async (row: ReceiveTableRow) => {
      if (row.shipmentId == null) return;
      setChildren(row.id, [buildPlaceholderRow(row.id, row.level, 'loading')]);

      const lines = await fetchShipmentBalances({
        shipmentId: row.shipmentId,
        rowId: row.id,
        level: row.level
      });

      if (!lines) {
        setChildren(row.id, [buildPlaceholderRow(row.id, row.level, 'error')]);
        return;
      }

      setChildren(row.id, lines.length ? lines : [buildPlaceholderRow(row.id, row.level, 'empty')]);
    },
    [fetchShipmentBalances, setChildren]
  );

  const rows = useMemo(() => buildReceiveRows(locations, childrenById), [locations, childrenById]);

  const handleRowExpand = useCallback(
    (rowId: string, expanded: boolean) => {
      setExpandedRows((prev) => {
        const next = new Set(prev);
        if (expanded) {
          next.add(rowId);
        } else {
          next.delete(rowId);
        }
        return next;
      });

      if (!expanded || requestedRef.current.has(rowId)) return;

      const row = rows.find((candidate) => candidate.id === rowId);
      // Date rows arrive with their consignments already attached, so only the
      // two lazily-backed kinds trigger a request.
      if (!row) return;
      if (row.rowType !== 'location' && row.rowType !== 'consignment') return;

      requestedRef.current.add(rowId);
      void (row.rowType === 'location' ? loadLocationChildren(row) : loadBalanceChildren(row));
    },
    [loadBalanceChildren, loadLocationChildren, rows]
  );

  return {
    rows,
    expandedRows,
    handleRowExpand
  };
};
